/**
 * What a line costs, and what the order costs — in the browser.
 *
 * The server is the authority: `@repo/core` re-reads the catalogue and
 * recomputes every figure at checkout. This copy exists so the amount above the
 * button moves on the same frame as the stepper. It is the one number on the
 * screen that must never look like it is waiting for a network.
 *
 * Keeping the two in step used to need a whole parity test suite, because both
 * sides ran three discount ladders and a two-way "which is cheaper" fork. There
 * are no ladders any more — a line is `price × quantity` on both sides — so the
 * two arithmetics are hard to make disagree.
 *
 * Everything here is net złoty as a float, matching the rest of the storefront.
 * Grosze and integers start at the server boundary — see `@repo/core/pricing`.
 */
import {
  type CoordinatorMode,
  coordinatorFeeMinor,
  coordinatorMode,
  coordinatorPieces,
} from '@repo/core/offering'
import { DEFAULT_PRICING_RULES, type PricingRuleValues, ruleValue } from '@repo/db/pricing-rules'
import { reelUnitNetMinor } from '@repo/db/reel-pack'
import { isShootDayLevel } from '@repo/db/shoot-day'
import { priceShoot } from '@repo/db/shoot-price'
import type { CreatorOfferingView, CreatorPick, CreatorVariantView } from '@/lib/creator'
import { footageFor, needsDate, needsRange, pickQuantity } from '@/lib/creator'
import { OPERATOR_SLUG, recordingRateOf, shootRateOf, shootScopeOf } from '@/lib/shoot-pricing'

export type LineMoney = {
  /** Net złoty for the line as configured. Zero when nothing can be priced yet. */
  total: number
  /**
   * Shown but not charged — a room by the hour, a crew day, an individual
   * quote. The server draws the same line, so the basket cannot promise a
   * number the checkout refuses to charge or hide one it will.
   */
  quoteOnly: boolean
  /** Equipment hire: priced in full, term still to confirm. */
  rental: boolean
  /**
   * The line holds a calendar — a room, a camera, a crew day.
   *
   * Separate from `rental`, which is only the day-laddered half of that, and
   * separate from `quoteOnly`, which a studio stopped being in I1. Spec §13
   * asks whether a line is a RESERVATION when it counts positions toward the
   * coordinator, and since I1 that is no longer answerable from the price.
   */
  booked: boolean
}

const NOTHING: LineMoney = {
  total: 0,
  quoteOnly: true,
  rental: false,
  booked: false,
}

/**
 * What this configured shoot costs, in ZŁOTY — or `null` when it is not one.
 *
 * `null`, not zero, and the difference matters: zero would be a free shoot and
 * this means „price it the old way". Everything that could be missing lands
 * there — no rate card on the offering, no day level chosen, no session or
 * scope or photo count, a row the owner has since withdrawn.
 *
 * Złoty because this module counts in them; `priceShoot` speaks grosze, so the
 * conversion happens here, once, at the boundary.
 */
function shootMoney(
  offering: CreatorOfferingView,
  variant: CreatorVariantView | undefined,
  pick: CreatorPick,
): number | null {
  if (!offering.shoot || !variant) return null
  if (!isShootDayLevel(variant.slug)) return null

  const photos = Number.parseInt(pick.shootPhotos, 10)
  if (!pick.shootSession || !pick.shootScope || !Number.isFinite(photos) || photos <= 0) return null

  const rate = shootRateOf(offering.shoot, pick.shootSession)
  const scope = shootScopeOf(offering.shoot, pick.shootSession, pick.shootScope)
  if (!rate || !scope) return null

  return priceShoot(rate, scope, photos, offering.shoot.margin).clientNetMinor / 100
}

/**
 * What a configured RECORDING day costs, in złoty — or `null`.
 *
 * The operator's twin of {@link shootMoney}, and `null` on one extra count: a
 * kind the owner has not priced yet. That is every kind today, so this returns
 * `null` throughout and the card says „wycena indywidualna" — which is what the
 * offer actually is, rather than a figure invented to fill the space.
 */
function recordingMoney(
  offering: CreatorOfferingView,
  variant: CreatorVariantView | undefined,
  pick: CreatorPick,
): number | null {
  if (!offering.shoot || !variant) return null
  if (!isShootDayLevel(variant.slug) || !pick.recordingType) return null
  const type = recordingRateOf(offering.shoot, pick.recordingType)
  if (!type?.isActive || type.clientNetMinor === null) return null
  return type.clientNetMinor / 100
}

/**
 * The least a shoot day can cost, across everything the model prices.
 *
 * What a tile and a card print when nobody has configured anything yet. The
 * catalogue has no figure to show for such a level — the photographer's plan
 * levels carry no price — and „wycena indywidualna" would be false about the
 * one thing on this site that quotes itself before the visitor has finished
 * answering.
 *
 * Grosze, like everything else here that ends in `NetMinor`.
 */
export function shootFloorNetMinor(offering: CreatorOfferingView): number | null {
  const model = offering.shoot
  if (!model) return null
  // The operator's day is not priced off the photography model, and reading it
  // there would print a session's „od …" on a camera operator's tile. His floor
  // is the cheapest kind of recording — none, while nothing is priced.
  if (offering.slug === OPERATOR_SLUG) {
    let lowest: number | null = null
    for (const type of model.recordingTypes) {
      if (!type.isActive || type.clientNetMinor === null) continue
      if (lowest === null || type.clientNetMinor < lowest) lowest = type.clientNetMinor
    }
    return lowest
  }
  let low: number | null = null
  for (const rate of model.rates) {
    for (const scope of model.scopes) {
      if (scope.session !== rate.session) continue
      const photos = scope.photos[0]
      if (photos === undefined) continue
      const { clientNetMinor } = priceShoot(rate, scope, photos, model.margin)
      if (low === null || clientNetMinor < low) low = clientNetMinor
    }
  }
  return low
}

/**
 * One configured line, priced.
 *
 * Three cases and the split is `bookingMode`, never what the offering is called
 * or which page it is sold on: a hire is the whole term at one price, a room or
 * a shoot day is an ESTIMATE until a human confirms the calendar, and
 * everything else is priced by the piece.
 */
export function lineMoney(
  offering: CreatorOfferingView,
  variant: CreatorVariantView | undefined,
  pick: CreatorPick,
  rules: PricingRuleValues = DEFAULT_PRICING_RULES,
): LineMoney {
  if (!variant) return NOTHING

  // ── A SHOOT DAY IS PRICED NOW, AND THE FLAG SAYS OTHERWISE ────────────
  // Asked BEFORE `quoteOnly`, which is the order this used to have backwards.
  // The photographer's plan levels carry no catalogue price any more, and the
  // schema makes that the same fact as `is_quote_only` — so a fully configured
  // session would have short-circuited to „Wycena indywidualna" in the basket
  // while the checkout charged 3 600 zł. The server asks in this order too
  // (`resolveCheckoutLines`), and the two must agree or the card is lying.
  //
  // This is the same `priceShoot` the site quoted with and the checkout freezes
  // with, so the figure under the card, the figure on the page and the figure
  // on the order are one derivation rather than three.
  const shoot = shootMoney(offering, variant, pick) ?? recordingMoney(offering, variant, pick)
  if (shoot !== null) {
    return { total: shoot, quoteOnly: false, rental: false, booked: needsDate(offering) }
  }

  if (variant.quoteOnly) return NOTHING
  const unit = variant.priceNet

  // ── a hire is a THRESHOLD (spec §11 · I4) ─────────────────────────────
  // The variant's price is the price of the whole term, so there is nothing
  // to multiply and nothing to ladder — the browser reads it exactly as the
  // checkout freezes it. This branch used to multiply a daily rate and apply a
  // `RENTAL_DAYS` rung, which is the browser doing arithmetic the server did
  // differently the moment the catalogue started storing thresholds.
  if (needsRange(offering)) {
    return { total: unit, quoteOnly: false, rental: true, booked: true }
  }

  const booked = needsDate(offering)

  // Still a question for a crew day nobody configured that way: an operator,
  // whose recording the owner has not priced yet, or a shoot added from
  // somewhere that does not ask.
  const stillAQuestion = offering.bookingMode === 'DAY'
  // `pickQuantity`, not the stepper: a film's quantity is its band's floor
  // (spec §8), and the server derives the same number from the same band.
  const qty = pickQuantity(offering, pick)
  // Ten reels of one level are the package — `reelUnitNetMinor` is the very
  // function the checkout freezes the line with, so the two cannot disagree.
  const lineUnit =
    (reelUnitNetMinor(offering.slug, variant.slug, qty, Math.round(unit * 100), rules) ?? 0) / 100

  // Spec §8's footage fee rides on the line as a passthrough — the same shape
  // `OrderLineInput.extraNetMinor` has on the server, so the estimate on the
  // screen and the frozen line total agree.
  const extra = (footageFor(offering, pick, rules)?.overNet ?? 0) / 100

  return { total: lineUnit * qty + extra, quoteOnly: stillAQuestion, rental: false, booked }
}

/** The least a line has to be for the order arithmetic to include it. */
export type PricedLine = {
  offeringSlug: string
  money: LineMoney
  /** Positions this line is worth toward spec §13's coordinator — see `pickPieces`. */
  pieces: number
}

export type OrderTotals = {
  /** Sum of the priced lines, before the coordinator fee. */
  subtotal: number
  total: number
  /** Lines with a figure that is not charged yet, and lines with no figure. */
  quoteCount: number
  /** Priced hires — the term is confirmed by a person before pick-up. */
  rentalCount: number

  // ── spec §13's coordinator (H3) ────────────────────────────────────────
  /** Positions that count toward the threshold; reservations are not among them. */
  positions: number
  /** Whether one may be bought, is already included, or is not offered here. */
  coordinatorMode: CoordinatorMode
  /** What one costs on this basket. 0 when compulsory — spec §13: "bezpłatnie". */
  coordinatorFee: number
  /** What is actually on the bill: the fee, or 0 when nobody asked for one. */
  coordinatorCharged: number
}

/**
 * The order, added up ONCE.
 *
 * One exclusion now: a quote-only line has no chargeable number at all. Hires
 * used to need a second rule — they were in the subtotal but could not earn the
 * basket bonus — and with no bonus to earn there is nothing to keep them out
 * of. Mirrors `cartTotals` in `@repo/core`.
 *
 * There is no discount here, and that is the whole shape of this function now.
 * A code is applied by the SERVER at checkout, against the total; the browser
 * never guesses at one, so the number above the button is the catalogue's.
 */
export function orderTotals(
  lines: readonly PricedLine[],
  /** Did the customer tick the coordinator? Ignored unless the basket may have one. */
  wantsCoordinator = false,
  rules: PricingRuleValues = DEFAULT_PRICING_RULES,
): OrderTotals {
  const priced = lines.filter((l) => !l.money.quoteOnly)
  const subtotal = priced.reduce((acc, l) => acc + l.money.total, 0)

  // ── the coordinator, on top of the basket (spec §13 · H3) ──────────────
  // The SAME rules the server applies, called directly rather than copied:
  // these are integer grosze functions, so the złoty float crosses the boundary
  // once in each direction and the two sides cannot drift over a threshold or a
  // floor. Everything else in this file is a mirror; this is the original.
  const coordinatorLines = lines.map((l) => ({
    pieces: l.pieces,
    isQuoteOnly: l.money.quoteOnly,
    // Every line with a calendar, not merely the hires: spec §13 says
    // "rezerwacje (studio, sprzęt, produkcja) nie liczą się do progu", and
    // since I1 a studio is priced, so the price no longer answers this.
    isReservation: l.money.booked,
    lineTotalNetMinor: Math.round(l.money.total * 100),
  }))
  const positions = coordinatorPieces(coordinatorLines)
  const mode = coordinatorMode(positions)
  // 12% of the per-piece half of the basket — and now simply that, because
  // there is no header-level bonus left to fold in first.
  const base = coordinatorLines
    .filter((l) => !l.isReservation && !l.isQuoteOnly)
    .reduce((acc, l) => acc + l.lineTotalNetMinor, 0)
  const coordinatorFee =
    coordinatorFeeMinor(base, mode, 'grosz-exact', {
      feeBps: ruleValue(rules, 'coordinator.fee_bps'),
      minFeeMinor: ruleValue(rules, 'coordinator.min_fee_net_minor'),
    }) / 100
  const charged =
    mode === 'COMPULSORY' || (mode === 'OPTIONAL' && wantsCoordinator) ? coordinatorFee : 0

  return {
    subtotal,
    total: subtotal + charged,
    quoteCount: lines.length - priced.length,
    rentalCount: priced.filter((l) => l.money.rental).length,
    positions,
    coordinatorMode: mode,
    coordinatorFee,
    coordinatorCharged: charged,
  }
}
